import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IngredientInput from './ingredient_input';
import IngredientLabel from './ingredient_label';
import Env from '../../env';
import IngredientStructuredDataEditPopup from './ingredient_structured_data_edit_popup';
import IngredientHeading from './ingredient_heading';
import { arraySectionFullTextIsHeading } from '../../services/ingredients';

const advancedRecipeEditorFeatures =
  Env.ADVANCED_RECIPE_EDITOR_ENABLED || false;

const IngredientLineContainer = ({
  inputId,
  ingredient,
  ingredientNutrition,
  suggestions,
  suggestionsLoading,
  disabled,
  onChange,
  onUpdateIngredientStructuredData,
  onDismiss,
  isEditing,
  clickToEditEnabled,
  onClickToEdit,
}) => {
  const labelRef = useRef(null);

  const [showStructuredDataEditPopup, setShowStructuredDataEditPopup] =
    useState(false);

  const onGoToStructuredDataEditPopup = () => {
    setShowStructuredDataEditPopup(true);
  };

  const onDismissIngredientStructuredData = () => {
    setShowStructuredDataEditPopup(false);
  };

  const handleUpdateIngredientStructuredData = (...args) => {
    setShowStructuredDataEditPopup(false);
    onUpdateIngredientStructuredData(...args);
  };

  const isHeading = arraySectionFullTextIsHeading(ingredient.fullText);

  if (!isEditing) {
    if (isHeading) {
      return (
        <IngredientHeading
          ingredient={ingredient}
          onClick={() => clickToEditEnabled && !disabled && onClickToEdit()}
        />
      );
    }
    return (
      <IngredientLabel
        ingredient={ingredient}
        strikeThrough={
          !clickToEditEnabled && ingredient.checkedInIngredientsList
        }
        highlightTokens={advancedRecipeEditorFeatures}
        onClick={() => clickToEditEnabled && !disabled && onClickToEdit()}
        editMode={clickToEditEnabled}
      />
    );
  }

  if (isEditing && showStructuredDataEditPopup) {
    return (
      <>
        <IngredientLabel
          ref={labelRef}
          ingredient={ingredient}
          strikeThrough={false}
          highlightTokens={advancedRecipeEditorFeatures}
          onClick={() => {}}
          editMode={false}
        />
        <IngredientStructuredDataEditPopup
          initialIngredient={ingredient}
          initialIngredientNutrition={ingredientNutrition}
          anchorEl={labelRef}
          onUpdate={handleUpdateIngredientStructuredData}
          onDismiss={onDismissIngredientStructuredData}
        />
      </>
    );
  }

  return (
    <IngredientInput
      inputId={inputId}
      ingredient={ingredient}
      suggestions={suggestions}
      suggestionsLoading={suggestionsLoading}
      disabled={disabled}
      onChange={onChange}
      onDismiss={onDismiss}
      onGoToDetail={onGoToStructuredDataEditPopup}
      isHeading={isHeading}
    />
  );
};

IngredientLineContainer.propTypes = {
  inputId: PropTypes.string.isRequired,
  ingredient: PropTypes.shape({
    fullText: PropTypes.string.isRequired,
    tokFullText: PropTypes.string,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        fromChar: PropTypes.number,
        toChar: PropTypes.number,
        type: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
    structuredIngredient: PropTypes.shape({
      quantity: PropTypes.number.isRequired,
      unitOfMeasure: PropTypes.string.isRequired,
      linkedIngredient: PropTypes.shape({
        linkedIngredientName: PropTypes.string,
      }),
    }).isRequired,
    checkedInIngredientsList: PropTypes.bool.isRequired,
  }).isRequired,
  ingredientNutrition: PropTypes.shape({}).isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      suggestionText: PropTypes.string.isRequired,
      suggestionType: PropTypes.string.isRequired,
      highlightTokens: PropTypes.shape({
        fromChar: PropTypes.number.isRequired,
        toChar: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  suggestionsLoading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onUpdateIngredientStructuredData: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  clickToEditEnabled: PropTypes.bool.isRequired,
  onClickToEdit: PropTypes.func.isRequired,
};

IngredientLineContainer.defaultProps = {
  disabled: false,
};

export default IngredientLineContainer;
